import React from "react";
import ScheduleChangesRadioButtons from "./schedule-changes-radio-buttons";
import { Typography } from "@procore/core-react";
import type { I18njs } from "@procore/core-i18n-js";
import TagsSlideToggleSet from "./tags-slide-toggle-set";
import ColumnWidthSegmentedController from "./column-width-segmented-controller";
import TaskCardDetailsSegmentedController from "./task-card-details-segmented-controller";
import {
   BOARDS_CONFIGURE_PANEL_UPDATE_EVENT,
   BOARDS_CONFIGURE_PANEL_KEY,
   maybeUpdateColumnWidths,
} from "../utils";
import { v4 } from "uuid";

export type BoardsConfigPanelValues = {
   showProjectNumber: boolean;
   showStartEndDate: boolean;
   showStartEndTime: boolean;
   showProjectRoles: boolean;
   showEstimatedAverageRate: boolean;
   showDailyRate: boolean;
   showDailyBurn: boolean;
   showResourceBreakdown: boolean;
   showProjectTags: boolean;
   showEquipment: boolean;
   showInactiveProjects: boolean;
   showJobTitle: boolean;
   showAllocationInformation: boolean;
   showRateInfo: boolean;
   showWorkDays: boolean;
   showTotalAssignmentRange: boolean;
   showGroups: boolean;
   showEmployeeID: boolean;
   showPhoneNumber: boolean;
   showAddress: boolean;
   showTags: boolean;
   showAllTags: boolean;
   columnWidth: string;
   showResourcePhoto: boolean;
   taskCardDetailMode: "compact" | "expanded";
};

export const defaultBoardsConfigurePanelValues: BoardsConfigPanelValues = {
   // Projects
   showProjectNumber: true,
   showStartEndDate: true,
   showStartEndTime: false,
   showProjectRoles: false,
   showEstimatedAverageRate: false,
   showDailyRate: false,
   showDailyBurn: false,
   showResourceBreakdown: false,
   showProjectTags: false,
   showEquipment: false,
   showInactiveProjects: false,
   // Assignments
   showJobTitle: true,
   showAllocationInformation: false,
   showRateInfo: false,
   showWorkDays: false,
   showTotalAssignmentRange: false,
   showGroups: false,
   showEmployeeID: false,
   showPhoneNumber: false,
   showAddress: false,
   showTags: false,
   showAllTags: false,
   showResourcePhoto: false,
   columnWidth: "small",
   taskCardDetailMode: "compact",
} as const;

// These values are used to populate the initial state of the configure menu on page load
export function getBoardsConfigurePanelValues(): BoardsConfigPanelValues {
   const savedBoardsConfigurePanelState = localStorage.getItem(BOARDS_CONFIGURE_PANEL_KEY);
   return savedBoardsConfigurePanelState
      ? {
           ...defaultBoardsConfigurePanelValues,
           ...JSON.parse(savedBoardsConfigurePanelState),
        }
      : { ...defaultBoardsConfigurePanelValues };
}

export function updateBoardsConfigurePanelLocalStorage<K extends keyof BoardsConfigPanelValues>(
   key: K,
   value: BoardsConfigPanelValues[K],
) {
   const config: Partial<BoardsConfigPanelValues> =
      JSON.parse(localStorage.getItem(BOARDS_CONFIGURE_PANEL_KEY)!) ?? {};
   config[key] = value;
   localStorage.setItem(BOARDS_CONFIGURE_PANEL_KEY, JSON.stringify(config));

   // Dispatch a custom event to notify other components
   window.dispatchEvent(
      new CustomEvent(BOARDS_CONFIGURE_PANEL_UPDATE_EVENT, {
         detail: {
            key: BOARDS_CONFIGURE_PANEL_KEY,
            value: JSON.stringify(config),
         },
      }),
   );
}

export const COLUMN_WIDTHS: { [key: string]: number } = {
   small: 110,
   medium: 140,
   large: 190,
};

export default (I18n: I18njs) => {
   const updateColumnWidths = () => {
      maybeUpdateColumnWidths();
   };

   // Create a unique suffix to prevent duplicate IDs when component re-renders
   const uniqueSuffix = v4();

   return {
      type: "panel",
      dock: "right",
      header: false,
      collapsible: true,
      ui: "procore",
      cls: "b-sidebar",
      scrollable: { overflowY: true },
      collapsed: true,
      defaults: {
         labelPosition: "above",
         width: "15em",
      },
      items: [
         {
            tag: "span",
            html: I18n.t("views.company.workforce_planning.boards.configure_boards"),
         },
         {
            html: <ScheduleChangesRadioButtons />,
         },
         {
            html: (
               <div style={{ marginTop: "16px", marginBottom: "8px" }}>
                  <Typography intent="h3" style={{ fontSize: "14px" }}>
                     {I18n.t("views.company.workforce_planning.boards.configure_panel.width")}
                  </Typography>
               </div>
            ),
         },
         {
            html: <ColumnWidthSegmentedController updateColumnWidths={updateColumnWidths} />,
         },
         {
            html: (
               <div style={{ marginTop: "16px" }}>
                  <Typography intent="h3" style={{ fontSize: "14px" }}>
                     {I18n.t(
                        "views.company.workforce_planning.boards.configure_panel.view_options",
                     )}
                  </Typography>
               </div>
            ),
         },
         {
            html: (
               <div style={{ marginTop: "8px" }}>
                  <Typography intent="small">
                     {I18n.t("views.company.workforce_planning.project")}
                  </Typography>
               </div>
            ),
         },
         {
            type: "slidetoggle",
            label: I18n.t("views.company.workforce_planning.people.project_number"),
            labelPosition: "after",
            value: getBoardsConfigurePanelValues().showProjectNumber,
            onChange: ({ value }: any) => {
               updateBoardsConfigurePanelLocalStorage("showProjectNumber", value);
            },
            id: `boards-configure-project-number${uniqueSuffix}`,
         },
         {
            type: "slidetoggle",
            label: I18n.t("views.company.workforce_planning.boards.configure_panel.start_end_date"),
            labelPosition: "after",
            value: getBoardsConfigurePanelValues().showStartEndDate,
            onChange: ({ value }: any) => {
               updateBoardsConfigurePanelLocalStorage("showStartEndDate", value);
            },
            id: `boards-configure-start-end-date${uniqueSuffix}`,
         },
         {
            type: "slidetoggle",
            label: I18n.t(
               "views.company.workforce_planning.boards.configure_panel.start_end_times",
            ),
            labelPosition: "after",
            value: getBoardsConfigurePanelValues().showStartEndTime,
            onChange: ({ value }: any) => {
               updateBoardsConfigurePanelLocalStorage("showStartEndTime", value);
            },
            id: `boards-configure-start-end-time${uniqueSuffix}`,
         },
         // TODO: Add project roles back in when designs are ready
         // {
         //    type: "slidetoggle",
         //    label: "Project Role(s)",
         //    labelPosition: "after",
         //    value: getBoardsConfigurePanelValues().showProjectRoles,
         //    onChange: ({ value }: any) => {
         //       updateBoardsConfigurePanelLocalStorage("showProjectRoles", value);
         //    },
         //    id: "boards-configure-project-roles",
         // },
         {
            type: "slidetoggle",
            label: I18n.t(
               "views.company.workforce_planning.boards.configure_panel.estimated_average_rate",
            ),
            labelPosition: "after",
            value: getBoardsConfigurePanelValues().showEstimatedAverageRate,
            onChange: ({ value }: any) => {
               updateBoardsConfigurePanelLocalStorage("showEstimatedAverageRate", value);
            },
            id: `boards-configure-estimated-average-rate${uniqueSuffix}`,
         },
         {
            type: "slidetoggle",
            label: I18n.t("views.company.workforce_planning.boards.configure_panel.daily_rate"),
            labelPosition: "after",
            value: getBoardsConfigurePanelValues().showDailyRate,
            onChange: ({ value }: any) => {
               updateBoardsConfigurePanelLocalStorage("showDailyRate", value);
            },
            id: `boards-configure-daily-rate${uniqueSuffix}`,
         },
         {
            type: "slidetoggle",
            label: I18n.t("views.company.workforce_planning.boards.configure_panel.daily_burn"),
            labelPosition: "after",
            value: getBoardsConfigurePanelValues().showDailyBurn,
            onChange: ({ value }: any) => {
               updateBoardsConfigurePanelLocalStorage("showDailyBurn", value);
            },
            id: `boards-configure-daily-burn${uniqueSuffix}`,
         },
         {
            type: "slidetoggle",
            label: I18n.t("views.company.workforce_planning.boards.configure_panel.project_tags"),
            labelPosition: "after",
            value: getBoardsConfigurePanelValues().showProjectTags,
            onChange: ({ value }: any) => {
               updateBoardsConfigurePanelLocalStorage("showProjectTags", value);
            },
            id: `boards-configure-project-tags${uniqueSuffix}`,
         },
         {
            type: "slidetoggle",
            label: I18n.t("views.company.workforce_planning.resource_breakdown"),
            labelPosition: "after",
            value: getBoardsConfigurePanelValues().showResourceBreakdown,
            onChange: ({ value }: any) => {
               updateBoardsConfigurePanelLocalStorage("showResourceBreakdown", value);
            },
            id: `boards-configure-resource-breakdown${uniqueSuffix}`,
         },

         // TODO: Add equipment and inactive projects back in when designs are ready
         // {
         //    type: "slidetoggle",
         //    label: "Equipment",
         //    labelPosition: "after",
         //    value: getBoardsConfigurePanelValues().showEquipment,
         //    onChange: ({ value }: any) => {
         //       updateBoardsConfigurePanelLocalStorage("showEquipment", value);
         //    },
         //    id: "boards-configure-equipment",
         // },
         // {
         //    type: "slidetoggle",
         //    label: "Inactive Projects",
         //    labelPosition: "after",
         //    value: getBoardsConfigurePanelValues().showInactiveProjects,
         //    onChange: ({ value }: any) => {
         //       updateBoardsConfigurePanelLocalStorage("showInactiveProjects", value);
         //    },
         //    id: "boards-configure-inactive-projects",
         // },
         {
            html: (
               <div style={{ marginTop: "16px" }}>
                  <Typography intent="small">
                     {I18n.t("views.company.workforce_planning.resource")}
                  </Typography>
               </div>
            ),
         },
         {
            html: <TaskCardDetailsSegmentedController uniqueSuffix={uniqueSuffix} />,
         },
         {
            type: "slidetoggle",
            label: I18n.t("views.company.workforce_planning.boards.configure_panel.employee_id"),
            labelPosition: "after",
            value: getBoardsConfigurePanelValues().showEmployeeID,
            onChange: ({ value }: any) => {
               updateBoardsConfigurePanelLocalStorage("showEmployeeID", value);
            },
            id: `boards-configure-employee-id${uniqueSuffix}`,
         },
         {
            type: "slidetoggle",
            label: I18n.t("views.company.workforce_planning.people.job_title"),
            labelPosition: "after",
            value: getBoardsConfigurePanelValues().showJobTitle,
            onChange: ({ value }: any) => {
               updateBoardsConfigurePanelLocalStorage("showJobTitle", value);
            },
            id: `boards-configure-job-title${uniqueSuffix}`,
         },
         {
            type: "slidetoggle",
            label: I18n.t("views.company.workforce_planning.boards.configure_panel.phone_number"),
            labelPosition: "after",
            value: getBoardsConfigurePanelValues().showPhoneNumber,
            onChange: ({ value }: any) => {
               updateBoardsConfigurePanelLocalStorage("showPhoneNumber", value);
            },
            id: `boards-configure-phone-number${uniqueSuffix}`,
         },
         // TODO: Add groups back in when designs are ready
         // {
         //    type: "slidetoggle",
         //    label: "Groups",
         //    labelPosition: "after",
         //    value: getBoardsConfigurePanelValues().showGroups,
         //    onChange: ({ value }: any) => {
         //       updateBoardsConfigurePanelLocalStorage("showGroups", value);
         //    },
         //    id: "boards-configure-groups",
         // },
         // These toggles are grouped in a component since visibility is conditional.
         {
            html: <TagsSlideToggleSet />,
         },
         {
            type: "slidetoggle",
            label: I18n.t(
               "views.company.workforce_planning.boards.configure_panel.total_assignment_range",
            ),
            labelPosition: "after",
            value: getBoardsConfigurePanelValues().showTotalAssignmentRange,
            onChange: ({ value }: any) => {
               updateBoardsConfigurePanelLocalStorage("showTotalAssignmentRange", value);
            },
            id: `boards-configure-total-assignment-range${uniqueSuffix}`,
         },
         // TODO: Add work days back in when designs are ready
         // {
         //    type: "slidetoggle",
         //    label: "Work Days",
         //    labelPosition: "after",
         //    value: getBoardsConfigurePanelValues().showWorkDays,
         //    onChange: ({ value }: any) => {
         //       updateBoardsConfigurePanelLocalStorage("showWorkDays", value);
         //    },
         //    id: "boards-configure-work-days",
         // },
         {
            type: "slidetoggle",
            label: I18n.t(
               "views.company.workforce_planning.boards.configure_panel.allocation_information",
            ),
            labelPosition: "after",
            value: getBoardsConfigurePanelValues().showAllocationInformation,
            onChange: ({ value }: any) => {
               updateBoardsConfigurePanelLocalStorage("showAllocationInformation", value);
            },
            id: `boards-configure-allocation-information${uniqueSuffix}`,
         },
         {
            type: "slidetoggle",
            label: I18n.t("views.company.workforce_planning.boards.configure_panel.rate_info"),
            labelPosition: "after",
            value: getBoardsConfigurePanelValues().showRateInfo,
            onChange: ({ value }: any) => {
               updateBoardsConfigurePanelLocalStorage("showRateInfo", value);
            },
            id: `boards-configure-rate-info${uniqueSuffix}`,
         },
         {
            type: "slidetoggle",
            label: I18n.t("views.company.workforce_planning.boards.configure_panel.address"),
            labelPosition: "after",
            value: getBoardsConfigurePanelValues().showAddress,
            onChange: ({ value }: any) => {
               updateBoardsConfigurePanelLocalStorage("showAddress", value);
            },
            id: `boards-configure-address${uniqueSuffix}`,
         },
         {
            type: "slidetoggle",
            label: I18n.t(
               "views.company.workforce_planning.boards.configure_panel.show_resource_photo",
            ),
            labelPosition: "after",
            value: getBoardsConfigurePanelValues().showResourcePhoto,
            onChange: ({ value }: any) => {
               updateBoardsConfigurePanelLocalStorage("showResourcePhoto", value);
            },
            id: `boards-configure-resource-photo${uniqueSuffix}`,
         },
      ],
   };
};
